import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useDeploymentSettingsApi } from '@/api/deployment-settings/useDeploymentSettingsApi';
import {
    APP_MODULES,
    defaultModuleDeploymentSettings,
    type AppModule,
} from '@/config/maintenanceModules';
import type { DeploymentSettings, ModuleDeploymentSetting } from '@/models/deploymentSettings';

function normalizeModuleSetting(
    base: ModuleDeploymentSetting,
    patch?: Partial<ModuleDeploymentSetting>
): ModuleDeploymentSetting {
    const merged = { ...base, ...patch };
    return {
        visibleToAll: merged.visibleToAll ?? base.visibleToAll,
        underMaintenance: merged.underMaintenance ?? base.underMaintenance,
        allowedUserEmails: merged.allowedUserEmails ?? [],
        maintenanceBypassEmails: merged.maintenanceBypassEmails ?? [],
    };
}

function normalizeSettings(data?: DeploymentSettings): Record<AppModule, ModuleDeploymentSetting> {
    const modules = {} as Record<AppModule, ModuleDeploymentSetting>;

    for (const key of APP_MODULES) {
        modules[key] = normalizeModuleSetting(
            defaultModuleDeploymentSettings[key],
            data?.modules?.[key]
        );
    }

    return modules;
}

export const useDeploymentSettingsStore = defineStore('deploymentSettings', () => {
    const modules = ref<Record<AppModule, ModuleDeploymentSetting>>(
        normalizeSettings()
    );
    const loaded = ref(false);
    const loading = ref(false);
    const saving = ref(false);

    const { getDeploymentSettings, saveDeploymentSettings } = useDeploymentSettingsApi();

    const settingsPayload = computed<DeploymentSettings>(() => ({
        modules: modules.value,
    }));

    async function fetchSettings(force = false) {
        if (loaded.value && !force) {
            return;
        }

        loading.value = true;
        try {
            const response = await getDeploymentSettings();
            if (response.status === 'success' && response.data) {
                modules.value = normalizeSettings(response.data);
                loaded.value = true;
            }
        } finally {
            loading.value = false;
        }
    }

    async function saveSettings() {
        saving.value = true;
        try {
            const response = await saveDeploymentSettings(settingsPayload.value);
            if (response.status === 'success' && response.data) {
                modules.value = normalizeSettings(response.data);
                loaded.value = true;
            }
            return response;
        } finally {
            saving.value = false;
        }
    }

    function getModuleSetting(module: AppModule): ModuleDeploymentSetting {
        return modules.value[module] ?? defaultModuleDeploymentSettings[module];
    }

    function isModuleVisibleToAll(module: AppModule): boolean {
        return getModuleSetting(module).visibleToAll;
    }

    function isModuleUnderMaintenance(module: AppModule): boolean {
        return getModuleSetting(module).underMaintenance;
    }

    function canBypassMaintenance(module: AppModule, userEmail?: string): boolean {
        const email = (userEmail ?? '').toLowerCase();
        if (!email) {
            return false;
        }
        return getModuleSetting(module).maintenanceBypassEmails.some(
            (allowed) => allowed.toLowerCase() === email
        );
    }

    /** True when the user should see the maintenance panel instead of module content. */
    function shouldShowMaintenanceForUser(
        module: AppModule,
        userEmail?: string
    ): boolean {
        if (!isModuleUnderMaintenance(module)) {
            return false;
        }
        return !canBypassMaintenance(module, userEmail);
    }

    function canUserAccessModule(
        module: AppModule,
        userType: number,
        userEmail?: string
    ): boolean {
        const setting = getModuleSetting(module);
        if (setting.visibleToAll) {
            return true;
        }

        const email = (userEmail ?? '').toLowerCase();
        if (!email) {
            return false;
        }

        return setting.allowedUserEmails.some(
            (allowed) => allowed.toLowerCase() === email
        );
    }

    function updateModuleSetting(
        module: AppModule,
        patch: Partial<ModuleDeploymentSetting>
    ) {
        modules.value[module] = normalizeModuleSetting(
            getModuleSetting(module),
            patch
        );
    }

    return {
        modules,
        loaded,
        loading,
        saving,
        settingsPayload,
        fetchSettings,
        saveSettings,
        getModuleSetting,
        isModuleVisibleToAll,
        isModuleUnderMaintenance,
        shouldShowMaintenanceForUser,
        canBypassMaintenance,
        canUserAccessModule,
        updateModuleSetting,
    };
});
